import React, { useContext } from "react";
import { useParams } from "react-router-dom";

import { UserContext, SettingsContext, useEntitlements } from "../contexts/index.jsx";
import { PageHeader, PageLayout, StatePanel } from "../components/ui";
import { MarketingView } from "../views/DailyRecap/MarketingView/MarketingView";
import AccountingView from "../views/Accounting/AccountingView";
import { ACCOUNTING, canAccounting } from "../helpers/accountingAccess";

import "./analytics-page.scss";
import { HOME_PATH } from "../core/config/dashboard.js";

const TABS = [
  // Accounting tab / PnL / Reconciliation (Feature 41) — Future Release, so
  // hidden from tenants; DAO keeps it via the platform-store exemption.
  { label: "Accounting",        slug: "accounting", feature: "accounting_pnl_reconciliation" },
  // Store Performance / Sales Rep / Executive View moved to the Dashboard
  // (Dashboard v2, 2026-10-21): its Store / My Day / Executive tabs. The old
  // /analytics/<slug> URLs redirect there (layouts/Routes.jsx, ahead of the
  // /analytics/:tab route). The DailyRecap view components are kept — other
  // code still imports them — they are just no longer routed from here.
  // CRM Analytics / Social Media / Payroll share one Coming soon matrix row
  // (Alex, 2026-10-28): DAO Watches keeps the placeholders, every other store
  // has them hidden in every entitlements mode.
  { label: "CRM Analytics",     slug: "crm-analytics", feature: "analytics_crm_social_payroll" },
  // Marketing/GA analytics (Feature 50) — Black.
  { label: "Marketing",         slug: "marketing", feature: "analytics_marketing" },
  { label: "Social Media",      slug: "social-media", feature: "analytics_crm_social_payroll" },
  { label: "Payroll",           slug: "payroll", feature: "analytics_crm_social_payroll" },
];

const ComingSoonView = ({ label }) => (
  <StatePanel
    state="empty"
    icon="Clock"
    title={label}
    description="This section is coming soon. We're building it now."
  />
);

export function AnalyticsPage({ user: propUser }) {
  const ctx  = useContext(UserContext);
  const user = propUser || ctx?.user;
  const { tab } = useParams();
  const settings = useContext(SettingsContext);

  // Hide the Accounting tab entirely for users without "Accounting tab view".
  // Plan gating is a second, independent condition: a tab declaring a
  // `feature` also needs the store's plan to include it.
  const canAccountingTab = canAccounting(user, ACCOUNTING.TAB);
  const { hasFeature } = useEntitlements();
  const visibleTabs = TABS
    .filter((t) => canAccountingTab || t.slug !== "accounting")
    .filter((t) => !t.feature || hasFeature(t.feature));
  // `/analytics` lands on the first tab this user can see — Accounting when
  // its grant + feature allow, then CRM Analytics, then Marketing. A typed URL
  // for a hidden tab (e.g. /analytics/payroll off DAO) lands there too, rather
  // than rendering the hidden tab's content.
  const currentTab = visibleTabs.some((t) => t.slug === tab) ? tab : visibleTabs[0]?.slug;

  // Iframe embeds carry state through the URL, so every tab link repeats the
  // same query suffix — built once here, same shape as Dashboard.jsx's tabs.
  const query = `?${settings.iframeQueryParams || ""}`;
  const tabItems = visibleTabs.map((t) => ({ label: t.label, to: `/analytics/${t.slug}${query}`, exact: true }));

  return (
    <PageLayout className="my-store-page">
      {/* Standard page chrome. The route is registered with `noTopBar`, so the
          RCAdminLayout top bar is suppressed and the page supplies its own —
          same arrangement as Manage Listings and Orders. The section tabs ride
          in the header's sub-bar, so this page reads title → tabs → content
          exactly like every other page. */}
      <PageHeader
        title="Analytics"
        description="Accounting, marketing and CRM reports. Store performance and sales live on the Dashboard."
        backTo={HOME_PATH}
        tabs={tabItems}
        activePath={`/analytics/${currentTab}${query}`}
      />

      <div className="my-store-page__content">
        {!currentTab && (
          <StatePanel
            state="empty"
            icon="Lock"
            title="No reports available"
            description="None of the Analytics reports are included for this store yet."
          />
        )}
        {currentTab === "accounting" && <AccountingView />}
        {currentTab === "crm-analytics" && <ComingSoonView label="CRM Analytics" />}
        {currentTab === "marketing"      && <MarketingView />}
        {currentTab === "social-media"   && <ComingSoonView label="Social Media"  />}
        {currentTab === "payroll"        && <ComingSoonView label="Payroll"       />}
      </div>
    </PageLayout>
  );
}
